import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  EditOutlined as EditIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowUp,
  MoreVert as MoreVertIcon,
  VisibilityOutlined as ViewIcon,
} from "@mui/icons-material";

import api from "../../services/api";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useTaskOverlay } from "../../utils/TaskOverlayContext";
import ProjectDialog from "../dialogs/ProjectDialog";
import TrackDialog from "../dialogs/TrackDialog";
import CreateEpicDialog from "../dialogs/EpicsDialog";
import TaskCreate from "../KeyComponents/TaskCreate";

/** Stable key for a division column ("none" for the No-Division column). */
const divisionKey = (divisionId) =>
  divisionId === null || divisionId === undefined ? "none" : String(divisionId);

/** Stable key for a department column group. */
const departmentKey = (group) =>
  group.department_id != null ? `d${group.department_id}` : `n:${group.department}`;

/** The 4 sprint slots offered by the sprint selector (see docs/sprint-slots-plan.md). */
const SCOPE_BACKLOG = "backlog";
const SCOPE_CURRENT = "current";
const SCOPE_NEXT = "next";
const SCOPE_BUCKET = "bucket";

const SPRINT_OPTIONS = [
  { value: SCOPE_BACKLOG, label: "Backlog" },
  { value: SCOPE_CURRENT, label: "Current" },
  { value: SCOPE_NEXT, label: "Next" },
  { value: SCOPE_BUCKET, label: "Bucket" },
];

const sprintScopeLabel = (value) =>
  SPRINT_OPTIONS.find((o) => o.value === value)?.label || "Current";

/**
 * Planning > Matrix view.
 *
 * Two-axis hierarchical grid: columns are Department > Division, rows are
 * Project > Track. Each cell shows open/total task counts for that (track,
 * division) intersection; clicking a cell opens a side drawer with that task
 * list. A sprint selector in the filter bar re-scopes every cell count.
 *
 * Departments and projects collapse/expand independently. Departments,
 * divisions, projects and tracks can be added or deleted in place. Clicking a
 * project label drills to the project view; a track label drills to track
 * detail.
 */
const MatrixView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { openTaskDetail } = useTaskOverlay();

  // Relative sprint scope: "previous" | "current" | "next" | "future".
  const [sprintValue, setSprintValue] = useState(SCOPE_CURRENT);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Independent collapse state for department columns, epic and project rows.
  const [collapsedDepts, setCollapsedDepts] = useState(() => new Set());
  const [collapsedEpics, setCollapsedEpics] = useState(() => new Set());
  const [collapsedProjects, setCollapsedProjects] = useState(() => new Set());

  // Drill-down drawer state.
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellTasks, setCellTasks] = useState([]);
  const [cellLoading, setCellLoading] = useState(false);

  // Entity-management UI state.
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [addEpicOpen, setAddEpicOpen] = useState(false);
  const [addTrackProjectId, setAddTrackProjectId] = useState(null);
  const [addTaskPrefill, setAddTaskPrefill] = useState(null); // { track, division, department }
  const [namePrompt, setNamePrompt] = useState(null); // { title, label, onSubmit }
  const [nameValue, setNameValue] = useState("");
  const [confirmState, setConfirmState] = useState(null); // { message, onConfirm }
  const [deptMenu, setDeptMenu] = useState(null); // { anchorEl, group }
  const [projectMenu, setProjectMenu] = useState(null); // { anchorEl, project }
  const [trackMenu, setTrackMenu] = useState(null); // { anchorEl, track }
  const [divisionMenu, setDivisionMenu] = useState(null); // { mouseX, mouseY, division }
  // View / edit dialog for departments and divisions (name + description).
  const [entityDialog, setEntityDialog] = useState(null); // { mode, type, id, name, description }
  const [entityName, setEntityName] = useState("");
  const [entityDescription, setEntityDescription] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [downloading, setDownloading] = useState(false);

  const workspaceId = currentWorkspace?.id;

  const showSnackbar = (message, severity = "error") =>
    setSnackbar({ open: true, message, severity });

  const sprintParams = useCallback(
    () => ({ sprint_scope: sprintValue }),
    [sprintValue],
  );

  const fetchMatrix = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("planning/matrix/", {
        params: { workspace_id: workspaceId, ...sprintParams() },
      });
      setMatrix(response.data);
    } catch (err) {
      console.error("Error fetching planning matrix:", err);
      setError("Could not load the matrix. Please try again.");
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, sprintParams]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  // Download the whole hierarchy (Epic > Project > Track and Department >
  // Division) with every description as a Markdown file.
  const downloadStructure = async () => {
    if (!workspaceId) return;
    setDownloading(true);
    try {
      const response = await api.get("planning/structure/export/", {
        params: { workspace_id: workspaceId },
        responseType: "blob",
      });
      const disposition = response.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : "workspace_structure.md";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting workspace structure:", err);
      showSnackbar("Could not download the structure. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Flatten the column groups into renderable body columns, honouring which
  // departments are collapsed (a collapsed department becomes one summary
  // column that rolls up its divisions).
  const flatColumns = useMemo(() => {
    if (!matrix) return [];
    const cols = [];
    matrix.column_groups.forEach((group) => {
      const dKey = departmentKey(group);
      if (collapsedDepts.has(dKey)) {
        cols.push({
          kind: "deptSummary",
          colKey: `sum-${dKey}`,
          dKey,
          group,
          divisionKeys: group.divisions.map((d) => divisionKey(d.division_id)),
        });
      } else if (group.divisions.length === 0) {
        cols.push({ kind: "empty", colKey: `empty-${dKey}`, dKey, group });
      } else {
        group.divisions.forEach((division) => {
          cols.push({
            kind: "div",
            colKey: `div-${dKey}-${divisionKey(division.division_id)}`,
            dKey,
            group,
            division,
            divKey: divisionKey(division.division_id),
          });
        });
      }
    });
    return cols;
  }, [matrix, collapsedDepts]);

  const totalColumns = flatColumns.length;
  const hasRows = matrix && matrix.row_groups.length > 0;

  const toggleDept = (dKey) =>
    setCollapsedDepts((prev) => {
      const next = new Set(prev);
      next.has(dKey) ? next.delete(dKey) : next.add(dKey);
      return next;
    });

  const toggleProject = (projectId) =>
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      next.has(projectId) ? next.delete(projectId) : next.add(projectId);
      return next;
    });

  const toggleEpic = (epicKey) =>
    setCollapsedEpics((prev) => {
      const next = new Set(prev);
      next.has(epicKey) ? next.delete(epicKey) : next.add(epicKey);
      return next;
    });

  // Group the project rows under their (primary) epic, preserving the
  // backend's alphabetical Epic > Project > Track ordering. Projects with no
  // epic collect under a trailing "No Epic" group.
  const epicGroups = useMemo(() => {
    if (!matrix) return [];
    const groups = [];
    const index = new Map();
    matrix.row_groups.forEach((project) => {
      const key = project.epic_id == null ? "none" : String(project.epic_id);
      let group = index.get(key);
      if (!group) {
        group = {
          epicKey: key,
          epicId: project.epic_id ?? null,
          epicName: project.epic_name || "No Epic",
          projects: [],
        };
        index.set(key, group);
        groups.push(group);
      }
      group.projects.push(project);
    });
    return groups;
  }, [matrix]);

  // Keys/ids used by the expand-all / collapse-all corner controls.
  const allDeptKeys = useMemo(
    () => (matrix ? matrix.column_groups.map(departmentKey) : []),
    [matrix],
  );
  const allProjectIds = useMemo(
    () => (matrix ? matrix.row_groups.map((p) => p.project_id) : []),
    [matrix],
  );

  const allDeptsCollapsed =
    allDeptKeys.length > 0 && allDeptKeys.every((k) => collapsedDepts.has(k));
  const allProjectsCollapsed =
    allProjectIds.length > 0 &&
    allProjectIds.every((id) => collapsedProjects.has(id));

  // Collapse every department column (horizontal axis) or, if already all
  // collapsed, expand them back.
  const toggleAllDepts = () =>
    setCollapsedDepts(allDeptsCollapsed ? new Set() : new Set(allDeptKeys));

  // Collapse every project row (vertical axis) or expand them all back.
  const toggleAllProjects = () =>
    setCollapsedProjects(
      allProjectsCollapsed ? new Set() : new Set(allProjectIds),
    );

  // Resolve a cell's open/total for a given flat column.
  const cellValueFor = (trackCells, col) => {
    if (col.kind === "div") {
      const c = trackCells[col.divKey];
      return { total: c?.total || 0, open: c?.open || 0 };
    }
    if (col.kind === "deptSummary") {
      let total = 0;
      let open = 0;
      col.divisionKeys.forEach((k) => {
        const c = trackCells[k];
        if (c) {
          total += c.total;
          open += c.open;
        }
      });
      return { total, open };
    }
    return { total: 0, open: 0 };
  };

  // Horizontal total for a track row: sum across every visible column.
  const rowTotalFor = (trackCells) =>
    flatColumns.reduce(
      (acc, col) => {
        const v = cellValueFor(trackCells, col);
        acc.total += v.total;
        acc.open += v.open;
        return acc;
      },
      { total: 0, open: 0 },
    );

  // Vertical totals per column (one per division/roll-up) plus the grand
  // total, summed across every track row. Keyed by flat column key.
  const columnTotals = useMemo(() => {
    const perColumn = {};
    const grand = { total: 0, open: 0 };
    flatColumns.forEach((col) => {
      perColumn[col.colKey] = { total: 0, open: 0 };
    });
    if (matrix) {
      matrix.row_groups.forEach((project) => {
        project.tracks.forEach((track) => {
          const trackCells = matrix.cells[String(track.track_id)] || {};
          flatColumns.forEach((col) => {
            const v = cellValueFor(trackCells, col);
            perColumn[col.colKey].total += v.total;
            perColumn[col.colKey].open += v.open;
            grand.total += v.total;
            grand.open += v.open;
          });
        });
      });
    }
    return { perColumn, grand };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, flatColumns]);

  // Shared open/total badge used by cells and total rows/columns.
  const renderCount = (value) =>
    value.total > 0 ? (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 0.25,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            color: value.open > 0 ? "warning.main" : "success.main",
          }}
        >
          {value.open}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary">
          /{value.total}
        </Typography>
      </Box>
    ) : (
      <Typography component="span" color="text.disabled">
        –
      </Typography>
    );

  const handleCellClick = (track, col, value) => {
    if (col.kind === "deptSummary") {
      // Roll-ups span multiple divisions; expand instead of drilling.
      toggleDept(col.dKey);
      return;
    }
    if (col.kind !== "div" || value.total === 0) return;
    setSelectedCell({
      trackId: track.track_id,
      trackName: track.track_name,
      divisionId: col.division.division_id,
      divisionName: col.division.division_name,
    });
  };

  // Open the task creator pre-filled with this intersection's project, track,
  // division and department so a new task lands exactly where the user clicked.
  const openAddTask = (project, track, col) => {
    setAddTaskPrefill({
      project: project.project_id,
      track: track.track_id,
      division:
        col.kind === "div" && col.division.division_id != null
          ? col.division.division_id
          : "",
      department: col.group.department_id ?? "",
    });
  };

  // Load tasks whenever the selected cell or sprint scope changes.
  useEffect(() => {
    if (!selectedCell || !workspaceId) return;
    let cancelled = false;
    setCellLoading(true);
    setCellTasks([]);
    api
      .get("planning/matrix/tasks/", {
        params: {
          workspace_id: workspaceId,
          track_id: selectedCell.trackId,
          division_id: divisionKey(selectedCell.divisionId),
          ...sprintParams(),
        },
      })
      .then((response) => {
        if (!cancelled) setCellTasks(response.data?.payload || []);
      })
      .catch((err) => {
        console.error("Error fetching cell tasks:", err);
        if (!cancelled) setCellTasks([]);
      })
      .finally(() => {
        if (!cancelled) setCellLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCell, workspaceId, sprintParams]);

  const closeDrawer = () => setSelectedCell(null);

  // --- Entity management ----------------------------------------------------

  const openNamePrompt = (
    title,
    label,
    onSubmit,
    { initialValue = "", submitLabel = "Add" } = {},
  ) => {
    setNameValue(initialValue);
    setNamePrompt({ title, label, onSubmit, submitLabel });
  };

  const submitNamePrompt = async () => {
    const value = nameValue.trim();
    if (!value || !namePrompt) return;
    const { onSubmit } = namePrompt;
    setNamePrompt(null);
    await onSubmit(value);
  };

  const runDelete = (message, request) =>
    setConfirmState({
      message,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await request();
          fetchMatrix();
        } catch (err) {
          const detail =
            err?.response?.data?.detail || "Could not complete that action.";
          showSnackbar(detail, "error");
          console.error("Matrix delete failed:", err);
        }
      },
    });

  const addDepartment = () =>
    openNamePrompt("Add department", "Department name", async (name) => {
      try {
        await api.post("departments/", { name, workspace: workspaceId });
        fetchMatrix();
      } catch (err) {
        showSnackbar(
          err?.response?.data?.detail || "Could not add department.",
          "error",
        );
      }
    });

  const addDivision = (group) =>
    openNamePrompt(
      `Add division to ${group.department}`,
      "Division name",
      async (name) => {
        try {
          await api.post("divisions/", {
            name,
            department: group.department_id,
            workspace: workspaceId,
          });
          fetchMatrix();
        } catch (err) {
          showSnackbar(
            err?.response?.data?.detail || "Could not add division.",
            "error",
          );
        }
      },
    );

  const deleteDepartment = (group) =>
    runDelete(
      `Delete department "${group.department}"? Its divisions will be archived too.`,
      () =>
        api.delete(`departments/${group.department_id}/`, {
          params: { workspace_id: workspaceId },
        }),
    );

  const deleteDivision = (division) =>
    runDelete(`Delete division "${division.division_name}"?`, () =>
      api.delete(`divisions/${division.division_id}/`, {
        params: { workspace_id: workspaceId },
      }),
    );

  const renameProject = (project) =>
    openNamePrompt(
      "Rename project",
      "Project name",
      async (name) => {
        try {
          await api.patch(`projects/${project.project_id}/`, {
            name,
            workspace: workspaceId,
          });
          fetchMatrix();
        } catch (err) {
          showSnackbar(
            err?.response?.data?.detail || "Could not rename project.",
            "error",
          );
        }
      },
      { initialValue: project.project_name, submitLabel: "Save" },
    );

  const renameTrack = (track) =>
    openNamePrompt(
      "Rename track",
      "Track name",
      async (name) => {
        try {
          await api.patch(`tracks/${track.track_id}/`, {
            name,
            workspace: workspaceId,
          });
          fetchMatrix();
        } catch (err) {
          showSnackbar(
            err?.response?.data?.detail || "Could not rename track.",
            "error",
          );
        }
      },
      { initialValue: track.track_name, submitLabel: "Save" },
    );

  // Read-only detail modal for a department/division.
  const openViewEntity = (type, entity) =>
    setEntityDialog({ mode: "view", type, ...entity });

  // Edit form (name + description) for a department/division.
  const openEditEntity = (type, entity) => {
    setEntityName(entity.name || "");
    setEntityDescription(entity.description || "");
    setEntityDialog({ mode: "edit", type, id: entity.id, name: entity.name });
  };

  const submitEntityEdit = async () => {
    const name = entityName.trim();
    if (!name || !entityDialog) return;
    const { type, id } = entityDialog;
    const endpoint =
      type === "department" ? `departments/${id}/` : `divisions/${id}/`;
    try {
      await api.patch(endpoint, {
        name,
        description: entityDescription,
        workspace: workspaceId,
      });
      setEntityDialog(null);
      fetchMatrix();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.detail || `Could not save ${type}.`,
        "error",
      );
    }
  };

  const deleteProject = (project) =>
    runDelete(`Delete project "${project.project_name}"?`, () =>
      api.delete(`projects/${project.project_id}/`, {
        params: { workspace_id: workspaceId },
      }),
    );

  const deleteTrack = (track) =>
    runDelete(`Delete track "${track.track_name}"?`, () =>
      api.delete(`tracks/${track.track_id}/`, {
        params: { workspace_id: workspaceId },
      }),
    );

  const sprintLabel = `${sprintScopeLabel(sprintValue)} sprint`;

  return (
    <Box sx={{ p: 3 }}>
      {/* Filter bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Box />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="matrix-sprint-label">Sprint</InputLabel>
            <Select
              labelId="matrix-sprint-label"
              label="Sprint"
              value={sprintValue}
              onChange={(e) => setSprintValue(e.target.value)}
            >
              {SPRINT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Download the full hierarchy and descriptions as Markdown">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  downloading ? <CircularProgress size={16} /> : <DownloadIcon />
                }
                onClick={downloadStructure}
                disabled={downloading || !workspaceId}
              >
                Download
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddEpicOpen(true)}
          >
            Epic
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addDepartment}
          >
            Department
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddProjectOpen(true)}
          >
            Project
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ py: 4 }}>
          {error}
        </Typography>
      ) : !hasRows || totalColumns === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          Nothing to show yet. Add departments, projects, tracks and divisions to
          populate the matrix.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 220px)" }}>
          <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
            <TableHead>
              {/* Department group header row */}
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 4,
                    bgcolor: "background.paper",
                    minWidth: 240,
                    fontWeight: 700,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <span>Project › Track</span>
                    <Box sx={{ display: "flex", ml: "auto" }}>
                      <Tooltip
                        title={
                          allProjectsCollapsed
                            ? "Expand all rows"
                            : "Collapse all rows"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={toggleAllProjects}
                          aria-label="Toggle all project rows"
                        >
                          {allProjectsCollapsed ? (
                            <KeyboardDoubleArrowDown fontSize="small" />
                          ) : (
                            <KeyboardDoubleArrowUp fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          allDeptsCollapsed
                            ? "Expand all columns"
                            : "Collapse all columns"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={toggleAllDepts}
                          aria-label="Toggle all department columns"
                        >
                          {allDeptsCollapsed ? (
                            <KeyboardDoubleArrowRight fontSize="small" />
                          ) : (
                            <KeyboardDoubleArrowLeft fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </TableCell>
                {matrix.column_groups.map((group) => {
                  const dKey = departmentKey(group);
                  const collapsed = collapsedDepts.has(dKey);
                  const colSpan = collapsed
                    ? 1
                    : Math.max(group.divisions.length, 1);
                  const manageable = group.department_id != null;
                  return (
                    <TableCell
                      key={`dept-${dKey}`}
                      align="center"
                      colSpan={colSpan}
                      sx={{
                        fontWeight: 700,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.25,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleDept(dKey)}
                          aria-label="Toggle department"
                        >
                          {collapsed ? (
                            <KeyboardArrowRight fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )}
                        </IconButton>
                        <span>{group.department}</span>
                        {manageable && (
                          <>
                            <Tooltip title="Add division" arrow>
                              <IconButton
                                size="small"
                                onClick={() => addDivision(group)}
                              >
                                <AddIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                setDeptMenu({ anchorEl: e.currentTarget, group })
                              }
                            >
                              <MoreVertIcon fontSize="inherit" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
                {/* Grand-total column header, spanning both header rows. */}
                <TableCell
                  align="center"
                  rowSpan={2}
                  sx={{
                    position: "sticky",
                    right: 0,
                    zIndex: 5,
                    fontWeight: 700,
                    bgcolor: "background.paper",
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  Total
                </TableCell>
              </TableRow>

              {/* Division header row */}
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    top: 49,
                    zIndex: 4,
                    bgcolor: "background.paper",
                  }}
                />
                {flatColumns.map((col) => {
                  const isDivision =
                    col.kind === "div" && col.division.division_id != null;
                  return (
                  <TableCell
                    key={col.colKey}
                    align="center"
                    onContextMenu={
                      isDivision
                        ? (e) => {
                            e.preventDefault();
                            setDivisionMenu({
                              mouseX: e.clientX,
                              mouseY: e.clientY,
                              division: col.division,
                            });
                          }
                        : undefined
                    }
                    sx={{
                      top: 49,
                      fontWeight: 600,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      bgcolor: "background.paper",
                      borderLeft: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {col.kind === "div" ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Right-click for options" arrow>
                          <span>{col.division.division_name}</span>
                        </Tooltip>
                        {col.division.division_id != null && (
                          <Tooltip title="Delete division" arrow>
                            <IconButton
                              size="small"
                              onClick={() => deleteDivision(col.division)}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ) : col.kind === "deptSummary" ? (
                      <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                        {col.divisionKeys.length} divisions
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        no divisions
                      </Typography>
                    )}
                  </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {epicGroups.map((epic) => {
                const epicCollapsed = collapsedEpics.has(epic.epicKey);
                return (
                  <React.Fragment key={`epic-${epic.epicKey}`}>
                    {/* Epic group row */}
                    <TableRow>
                      <TableCell
                        colSpan={totalColumns + 2}
                        sx={{
                          bgcolor: "action.selected",
                          fontWeight: 800,
                          borderTop: `2px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            width: "fit-content",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleEpic(epic.epicKey)}
                            aria-label="Toggle epic"
                          >
                            {epicCollapsed ? (
                              <KeyboardArrowRight fontSize="small" />
                            ) : (
                              <KeyboardArrowDown fontSize="small" />
                            )}
                          </IconButton>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 800, letterSpacing: 0.3 }}
                          >
                            {epic.epicName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            ({epic.projects.length})
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {!epicCollapsed &&
                      epic.projects.map((project) => {
                        const collapsed = collapsedProjects.has(
                          project.project_id,
                        );
                        return (
                          <React.Fragment key={`proj-${project.project_id}`}>
                    {/* Project group row */}
                    <TableRow>
                      <TableCell
                        colSpan={totalColumns + 2}
                        sx={{
                          bgcolor: "action.hover",
                          fontWeight: 700,
                        }}
                      >
                        <Box
                          sx={{
                            // The cell spans every column, so making the cell
                            // itself sticky leaves it no room to slide within its
                            // row. Pin the label content instead so it stays
                            // frozen on horizontal scroll like the track column.
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            width: "fit-content",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            pl: 2,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleProject(project.project_id)}
                            aria-label="Toggle project"
                          >
                            {collapsed ? (
                              <KeyboardArrowRight fontSize="small" />
                            ) : (
                              <KeyboardArrowDown fontSize="small" />
                            )}
                          </IconButton>
                          <Tooltip title="Open project view" arrow>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() => navigate("/project-list")}
                            >
                              {project.project_name}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0.5 }}
                          >
                            ({project.tracks.length})
                          </Typography>
                          <Tooltip title="Add track" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setAddTrackProjectId(project.project_id)}
                            >
                              <AddIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              setProjectMenu({ anchorEl: e.currentTarget, project })
                            }
                          >
                            <MoreVertIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Track rows */}
                    {!collapsed &&
                      project.tracks.map((track) => {
                        const trackCells =
                          matrix.cells[String(track.track_id)] || {};
                        return (
                          <TableRow key={`track-${track.track_id}`} hover>
                            <TableCell
                              sx={{
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                bgcolor: "background.paper",
                                pl: 3,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Tooltip title="Open track detail" arrow>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      cursor: "pointer",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                    onClick={() =>
                                      navigate(`/tracks/${track.track_id}`)
                                    }
                                  >
                                    {track.track_name}
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    setTrackMenu({
                                      anchorEl: e.currentTarget,
                                      track,
                                    })
                                  }
                                >
                                  <MoreVertIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {flatColumns.map((col) => {
                              const value = cellValueFor(trackCells, col);
                              const clickable =
                                col.kind === "div" && value.total > 0;
                              return (
                                <TableCell
                                  key={`${track.track_id}-${col.colKey}`}
                                  align="center"
                                  onClick={() =>
                                    handleCellClick(track, col, value)
                                  }
                                  sx={{
                                    cursor: clickable ? "pointer" : "default",
                                    borderLeft: `1px solid ${theme.palette.divider}`,
                                    "&:hover": clickable
                                      ? { bgcolor: "action.selected" }
                                      : undefined,
                                    "&:hover .matrix-add": { opacity: 1 },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 0.25,
                                    }}
                                  >
                                    {value.total > 0 ? (
                                      <Tooltip
                                        title={`${value.open} open of ${value.total}`}
                                        arrow
                                      >
                                        <Box
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "baseline",
                                            gap: 0.25,
                                            fontVariantNumeric: "tabular-nums",
                                          }}
                                        >
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontWeight: 700,
                                              color:
                                                value.open > 0
                                                  ? "warning.main"
                                                  : "success.main",
                                            }}
                                          >
                                            {value.open}
                                          </Typography>
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            /{value.total}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Typography
                                        component="span"
                                        color="text.disabled"
                                      >
                                        –
                                      </Typography>
                                    )}
                                    {(col.kind === "div" || col.kind === "empty") && (
                                      <Tooltip title="Add task here" arrow>
                                        <IconButton
                                          className="matrix-add"
                                          size="small"
                                          sx={{
                                            p: 0.25,
                                            opacity: 0,
                                            transition: "opacity .15s",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAddTask(project, track, col);
                                          }}
                                        >
                                          <AddIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              );
                            })}

                            {/* Horizontal (per-track) total. */}
                            <TableCell
                              align="center"
                              sx={{
                                position: "sticky",
                                right: 0,
                                zIndex: 1,
                                bgcolor: "background.paper",
                                borderLeft: `2px solid ${theme.palette.divider}`,
                              }}
                            >
                              {renderCount(rowTotalFor(trackCells))}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}

              {/* Vertical (per-division) totals row. */}
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    bottom: 0,
                    zIndex: 3,
                    bgcolor: "action.hover",
                    fontWeight: 700,
                  }}
                >
                  Total
                </TableCell>
                {flatColumns.map((col) => (
                  <TableCell
                    key={`total-${col.colKey}`}
                    align="center"
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 2,
                      bgcolor: "action.hover",
                      fontWeight: 700,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {renderCount(columnTotals.perColumn[col.colKey] || { total: 0, open: 0 })}
                  </TableCell>
                ))}
                {/* Grand total (bottom-right corner). */}
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    right: 0,
                    bottom: 0,
                    zIndex: 3,
                    bgcolor: "action.hover",
                    fontWeight: 700,
                    borderLeft: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  {renderCount(columnTotals.grand)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Department context menu */}
      <Menu
        anchorEl={deptMenu?.anchorEl}
        open={Boolean(deptMenu)}
        onClose={() => setDeptMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const { group } = deptMenu;
            setDeptMenu(null);
            openViewEntity("department", {
              id: group.department_id,
              name: group.department,
              description: group.description,
            });
          }}
        >
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> View
        </MenuItem>
        <MenuItem
          onClick={() => {
            const { group } = deptMenu;
            setDeptMenu(null);
            openEditEntity("department", {
              id: group.department_id,
              name: group.department,
              description: group.description,
            });
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const { group } = deptMenu;
            setDeptMenu(null);
            addDivision(group);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add division
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            const { group } = deptMenu;
            setDeptMenu(null);
            deleteDepartment(group);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete department
        </MenuItem>
      </Menu>

      {/* Project context menu */}
      <Menu
        anchorEl={projectMenu?.anchorEl}
        open={Boolean(projectMenu)}
        onClose={() => setProjectMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const { project } = projectMenu;
            setProjectMenu(null);
            setAddTrackProjectId(project.project_id);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add track
        </MenuItem>
        <MenuItem
          onClick={() => {
            const { project } = projectMenu;
            setProjectMenu(null);
            renameProject(project);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename project
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            const { project } = projectMenu;
            setProjectMenu(null);
            deleteProject(project);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete project
        </MenuItem>
      </Menu>

      {/* Track context menu */}
      <Menu
        anchorEl={trackMenu?.anchorEl}
        open={Boolean(trackMenu)}
        onClose={() => setTrackMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const { track } = trackMenu;
            setTrackMenu(null);
            renameTrack(track);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename track
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            const { track } = trackMenu;
            setTrackMenu(null);
            deleteTrack(track);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete track
        </MenuItem>
      </Menu>

      {/* Division right-click context menu */}
      <Menu
        open={Boolean(divisionMenu)}
        onClose={() => setDivisionMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          divisionMenu
            ? { top: divisionMenu.mouseY, left: divisionMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            const d = divisionMenu.division;
            setDivisionMenu(null);
            openViewEntity("division", {
              id: d.division_id,
              name: d.division_name,
              description: d.description,
            });
          }}
        >
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> View
        </MenuItem>
        <MenuItem
          onClick={() => {
            const d = divisionMenu.division;
            setDivisionMenu(null);
            openEditEntity("division", {
              id: d.division_id,
              name: d.division_name,
              description: d.description,
            });
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            const d = divisionMenu.division;
            setDivisionMenu(null);
            deleteDivision(d);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* View / edit dialog for departments and divisions */}
      <Dialog
        open={Boolean(entityDialog)}
        onClose={() => setEntityDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "capitalize" }}>
          {entityDialog?.mode === "view"
            ? entityDialog?.type
            : `Edit ${entityDialog?.type || ""}`}
        </DialogTitle>
        <DialogContent>
          {entityDialog?.mode === "view" ? (
            <>
              <Typography variant="overline" color="text.secondary">
                Name
              </Typography>
              <Typography sx={{ mb: 2 }}>{entityDialog?.name || "—"}</Typography>
              <Typography variant="overline" color="text.secondary">
                Description
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {entityDialog?.description?.trim()
                  ? entityDialog.description
                  : "No description."}
              </Typography>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                label="Name"
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Description"
                multiline
                minRows={3}
                value={entityDescription}
                onChange={(e) => setEntityDescription(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {entityDialog?.mode === "view" ? (
            <>
              <Button
                onClick={() =>
                  openEditEntity(entityDialog.type, {
                    id: entityDialog.id,
                    name: entityDialog.name,
                    description: entityDialog.description,
                  })
                }
              >
                Edit
              </Button>
              <Button variant="contained" onClick={() => setEntityDialog(null)}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setEntityDialog(null)}>Cancel</Button>
              <Button variant="contained" onClick={submitEntityEdit}>
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Drill-down drawer */}
      <Drawer anchor="right" open={Boolean(selectedCell)} onClose={closeDrawer}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }} role="presentation">
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedCell?.trackName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCell?.divisionName} · {sprintLabel}
              </Typography>
            </Box>
            <IconButton onClick={closeDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {cellLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : cellTasks.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              No tasks at this intersection.
            </Typography>
          ) : (
            cellTasks.map((task) => (
              <Paper
                key={task.id}
                variant="outlined"
                onClick={() => openTaskDetail(task.id)}
                sx={{
                  p: 1.25,
                  mb: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {task.name}
                  </Typography>
                  <Chip label={task.status} size="small" />
                </Box>
                {task.assignee && (
                  <Typography variant="caption" color="text.secondary">
                    {task.assignee.full_name || task.assignee.username}
                  </Typography>
                )}
              </Paper>
            ))
          )}
        </Box>
      </Drawer>

      {/* Add epic / project / track dialogs */}
      <CreateEpicDialog
        open={addEpicOpen}
        onClose={() => setAddEpicOpen(false)}
        onSuccess={() => {
          setAddEpicOpen(false);
          fetchMatrix();
        }}
      />
      <ProjectDialog
        open={addProjectOpen}
        onClose={() => setAddProjectOpen(false)}
        onSuccess={() => {
          setAddProjectOpen(false);
          fetchMatrix();
        }}
      />
      {addTrackProjectId != null && (
        <TrackDialog
          open
          projectId={addTrackProjectId}
          onClose={() => setAddTrackProjectId(null)}
          onSuccess={() => {
            setAddTrackProjectId(null);
            fetchMatrix();
          }}
        />
      )}

      <TaskCreate
        open={Boolean(addTaskPrefill)}
        prefill={addTaskPrefill}
        onClose={() => setAddTaskPrefill(null)}
        onTaskCreated={fetchMatrix}
      />

      {/* Name prompt (department / division) */}
      <Dialog
        open={Boolean(namePrompt)}
        onClose={() => setNamePrompt(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{namePrompt?.title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={namePrompt?.label}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitNamePrompt();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNamePrompt(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitNamePrompt}>
            {namePrompt?.submitLabel || "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmState)} onClose={() => setConfirmState(null)}>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography>{confirmState?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmState?.onConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MatrixView;
